import { createContext, PropsWithChildren, useContext } from "react";

import Cookies from "js-cookie";
import _ from "lodash";

import { StargateClientOptions } from "@atlassiansox/bxp-react-stargate";

import { getReCaptchaKey } from "@/components/signup/utils";

import {
    errorHandleResponse,
    getAtlCohortIdFromCookie,
    getErrorMessage,
    isValidSite,
    mapCookieToHeaders,
    mapMeResponseToUserProfile,
    mapResponseToJson,
    mapResponseToText,
    PRODUCT_KEY_MAP,
    UnsuccessfulFetchError,
    MadlibContentGenerationError,
    ProjectCreateError,
    FASTSHIFT_STARGATE_BASE_URL,
    sendFastshiftSentryAlert
} from "../util";

import {
    ActivateDirectBuyOnOrgRequest,
    ActivateDirectBuyOnOrgResponse,
    ActivateDirectBuyProductRequest,
    ActivateDirectBuyProductResponse,
    ActivateProductRequest,
    ActivateProductResponse,
    ActivateProductUpstreamResponse,
    AisAvailableProductsOptions,
    AisSitesType,
    AsyncActivationStatusRequest,
    AsyncActivationStatusResponse,
    AsyncNewSiteActivationRequest,
    AvailableProductsLegacyResponse,
    AvailableProductsOptions,
    AvailableProductsResponse,
    AvailableProductsType,
    AvailableProductsWACResponse,
    BitbucketValidateWorkspaceSlugRequest,
    BitbucketValidateWorkspaceSlugResponse,
    BitbucketWorkspaceOptions,
    BitbucketWorkspacesResponse,
    CheckAuthenticationMethodsAvailableRequest,
    CheckAuthenticationMethodsAvailableResponse,
    CheckCloudNameAvailabilityRequest,
    CheckCloudNameAvailabilityResponse,
    CloudProductInfo,
    CloudSessionTokenCookieType,
    COFSProductActivateRequest,
    COFSProductActivateResponse,
    CreateOrgRequest,
    CreateOrgResponse,
    CreateSampleProjectPayload,
    CreateSampleProjectResponse,
    CustomerDomainEngagementExpectedError,
    CustomerDomainExpectedError,
    FastshiftUserDetailsExpectedError,
    FetchProjectRolesResponse,
    FetchTaskResponse,
    GetAccountOrgsRequest,
    GetAccountOrgsResponse,
    GetAccountProductsRequest,
    GetAccountProductsResponse,
    GetActivationAuthorizationRequest,
    GetActivationAuthorizationResponse,
    GetActivationRequest,
    GetActivationResponse,
    GetAdminhubAccountOrgsRequest,
    GetAdminhubAccountOrgsResponse,
    GetAdminhubOrgDetailsRequest,
    GetAdminhubOrgDetailsResponse,
    GetAvailableSitesRequest,
    GetAvailableSitesResponse,
    GetCcpHamsResponse,
    GetCcpHamsStatus,
    GetCloudProductInfoRequest,
    GetCloudStatusOptions,
    GetCloudStatusResponse,
    GetCustomerDomainEngagementRequest,
    GetCustomerDomainEngagementResponse,
    GetCustomerSupportRequestsPayload,
    GetFastshiftCustomerDomainRequest,
    GetFastshiftCustomerDomainResponse,
    GetFastshiftCustomerDomainsRequest,
    GetFastshiftCustomerDomainsResponse,
    GetFastshiftSupportRequestsRequest,
    GetFastshiftSupportRequestsResponse,
    GetFastshiftTopSupportRequestsRequest,
    GetFastshiftTopSupportRequestsResponse,
    GetFastshiftUserRequest,
    FastshiftPortalConsentRequestPayload,
    FastshiftPortalConsentSubmitPayload,
    GetFastshiftPortalUserConsentResponse,
    GetFastshiftUserResponse,
    GetInstancesPayload,
    GetInstancesResponse,
    GetInviteLinkRequest,
    GetInviteLinkResponse,
    GetL1AssessmentsPayload,
    GetL1AssessmentsResponse,
    GetLicenceInformationRequest,
    GetLicenseInformationResponse,
    GetMigrationPlanTotalsPayload,
    GetMigrationPlanTotalsResponse,
    GetOrgServersPayload,
    GetOrgServersResponse,
    GetRecommendedSiteNameRequest,
    GetRecommendedSiteNameResponse,
    GetSignupAccountOrgDetailsRequest,
    GetSignupAccountOrgDetailsResponse,
    GetUserBillingAdminTransactionAccounts,
    GetUserBillingAdminTransactionAccountsRequest,
    GetUserPermissionsIsPermittedRequest,
    GetUserPermissionsIsPermittedResponse,
    GetUserSegmentationRedirectAdviceRequest,
    GetUserSegmentationRedirectAdviceResponse,
    JiraProjectCreateErrors,
    JiraProjectCreateRequest,
    JiraProjectCreateResponse,
    LocaleType,
    MeOptions,
    POLL_TASK_TIMEOUT,
    PostCloudSignUpRequest,
    PostExpandFreeValidationRequest,
    PostExpandFreeValidationResponse,
    PostRequestAccessUserResourceRequest,
    PostRequestAccessUserResourceResponse,
    ProductActivateRequest,
    ProductActivateResponse,
    ProductActivationStatusRequest,
    ProductActivationStatusResponse,
    PutSampleProjectUserPropertiesPayload,
    SitesType,
    SubmitConfluenceOnboardingSelectionsRequest,
    SubmitConfluenceOnboardingSelectionsResponse,
    TaskError,
    ThemePreferencePayload,
    UpdateConsentPreferencesRequest,
    UpdateIdentityProfileParams,
    User,
    UserProfile,
    UserProfileOptions,
    UserProfileResponse,
    UserSignupRequest,
    UserSignupResponse,
    WizardCreateProjectPayload,
    WizardCreateProjectResponse,
    ActivateProductCrossflowRequest,
    ActivateProductCrossflowResponse,
    JobContext,
    WaitForActivateProductCrossflowRequest,
    WaitForActivateProductCrossflowResponse,
    MadlibContentGenerationRequest,
    MadlibContentGenerationResponse,
    ActivateProductCrossflowPlanResponse,
    JiraFFLContentGenerationRequest,
    JiraFFLContentGenerationResponse,
    EmailInviteResponse,
    EmailInviteRequest
} from "./use-bxp-web-stargate-client.types";

const BASE_URL = process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"] as string;

export type TesseractRuntimeSupportedRegions =
    | "ap-northeast-1"
    | "ap-northeast-2"
    | "ap-southeast-1"
    | "ap-southeast-2"
    | "ca-central-1"
    | "eu-central-1"
    | "eu-central-2"
    | "eu-west-1"
    | "eu-west-2"
    | "us-east-1"
    | "us-west-2";

export const BxpExpressServiceSupportedRegions = new Set<string>([
    "ap-southeast-1",
    "ap-southeast-2",
    "us-east-1",
    "us-west-2",
    "eu-west-1",
    "eu-central-1"
]);

export const BxpSignupServerServiceSupportedRegions = new Set<string>([
    "us-east-1",
    "us-west-2"
]);

export const CcpServiceSupportedRegions = new Set<string>(["us-east-1"]);

export interface IBxpWebStargateClient {
    activateProduct(
        request: ActivateProductRequest
    ): Promise<ActivateProductResponse>;

    availableProducts(
        options: AisAvailableProductsOptions
    ): Promise<AvailableProductsResponse>;

    availableProductsLegacy(
        options: AvailableProductsOptions
    ): Promise<AvailableProductsLegacyResponse>;

    bitbucketWorkspaces(
        options: BitbucketWorkspaceOptions
    ): Promise<BitbucketWorkspacesResponse>;

    checkAuthenticationMethodsAvailable(
        request: CheckAuthenticationMethodsAvailableRequest
    ): Promise<CheckAuthenticationMethodsAvailableResponse>;

    checkCloudNameAvailability(
        request: CheckCloudNameAvailabilityRequest
    ): Promise<CheckCloudNameAvailabilityResponse>;

    fetch(urlPath: string, init?: RequestInit): Promise<Response>;

    getActivationAuthorization(
        request: GetActivationAuthorizationRequest
    ): Promise<GetActivationAuthorizationResponse>;

    getAvailableSites(
        request: GetAvailableSitesRequest
    ): Promise<GetAvailableSitesResponse>;

    getCcpHamsStatus(request: GetCcpHamsStatus): Promise<GetCcpHamsResponse>;

    getCloudStatus(
        request: GetCloudStatusOptions
    ): Promise<GetCloudStatusResponse>;

    getLicenceInformationStatus(
        request: GetLicenceInformationRequest
    ): Promise<GetLicenseInformationResponse>;

    getProductActivationStatus(
        requires: ProductActivationStatusRequest
    ): Promise<ProductActivationStatusResponse>;

    getRecommendedSiteName(
        request: GetRecommendedSiteNameRequest
    ): Promise<GetRecommendedSiteNameResponse>;

    getSignupServerXsrfToken(): Promise<string>;

    getUserPermissionsIsPermitted(
        request: GetUserPermissionsIsPermittedRequest
    ): Promise<GetUserPermissionsIsPermittedResponse>;

    getUserProfile(options: UserProfileOptions): Promise<UserProfileResponse>;

    me(options: MeOptions): Promise<UserProfile>;

    updateIdentityProfile(
        patch: UpdateIdentityProfileParams
    ): Promise<undefined>;

    postCloudSignUp(payload: PostCloudSignUpRequest): Promise<unknown>;

    postCOFSProductActivate(
        request: COFSProductActivateRequest
    ): Promise<COFSProductActivateResponse>;

    postExpandFreeValidation(
        request: PostExpandFreeValidationRequest
    ): Promise<PostExpandFreeValidationResponse>;

    postJiraProjectCreate(
        request: JiraProjectCreateRequest
    ): Promise<JiraProjectCreateResponse>;

    postProductActivate(
        request: ProductActivateRequest
    ): Promise<ProductActivateResponse>;

    userSignup(request: UserSignupRequest): Promise<UserSignupResponse>;
    getConsentConfig(): Promise<LocaleType>;
    updateConsentPreferences(
        params: UpdateConsentPreferencesRequest
    ): Promise<unknown>;

    getInviteLink(params: GetInviteLinkRequest): Promise<GetInviteLinkResponse>;

    submitConfluenceOnboardingSelections(
        postData: SubmitConfluenceOnboardingSelectionsRequest
    ): Promise<SubmitConfluenceOnboardingSelectionsResponse>;

    postUserSegmentationViewAttempt(): Promise<unknown>;

    getUserSegmentationRedirectAdvice(
        params: GetUserSegmentationRedirectAdviceRequest
    ): Promise<GetUserSegmentationRedirectAdviceResponse>;

    postRequestAccessUserResource(
        params: PostRequestAccessUserResourceRequest
    ): Promise<PostRequestAccessUserResourceResponse>;

    user(): Promise<User | undefined>;

    getAccountOrgs(
        request: GetAccountOrgsRequest
    ): Promise<GetAccountOrgsResponse>;

    getAdminhubAccountOrgs(
        request: GetAdminhubAccountOrgsRequest
    ): Promise<GetAdminhubAccountOrgsResponse>;

    getAdminhubOrgDetails(
        request: GetAdminhubOrgDetailsRequest
    ): Promise<GetAdminhubOrgDetailsResponse>;

    activateDirectBuyOnOrg(
        request: ActivateDirectBuyOnOrgRequest
    ): Promise<ActivateDirectBuyOnOrgResponse>;

    getAccountProducts(
        request: GetAccountProductsRequest
    ): Promise<GetAccountProductsResponse>;

    getActivationType(
        request: GetActivationRequest
    ): Promise<GetActivationResponse>;

    cofsCreateOrg(request: CreateOrgRequest): Promise<CreateOrgResponse>;

    bitbucketValidateWorkspaceSlug(
        request: BitbucketValidateWorkspaceSlugRequest
    ): Promise<BitbucketValidateWorkspaceSlugResponse>;

    getCloudProductInfo(
        request: GetCloudProductInfoRequest
    ): Promise<CloudProductInfo>;

    activateDirectBuyProduct(
        request: ActivateDirectBuyProductRequest
    ): Promise<ActivateDirectBuyProductResponse>;

    sampleProjectCreate(
        payload: CreateSampleProjectPayload
    ): Promise<CreateSampleProjectResponse>;

    wizardProjectCreate(
        payload: WizardCreateProjectPayload
    ): Promise<WizardCreateProjectResponse>;

    putThemePreference(payload: ThemePreferencePayload): Promise<boolean>;

    putSampleProjectUserProperties(
        payload: PutSampleProjectUserPropertiesPayload
    ): Promise<boolean>;

    pollTaskUntilComplete(
        taskId: string,
        cloudId: string,
        TIMEOUT?: number
    ): Promise<FetchTaskResponse>;

    fetchProjectRoles(
        cloudId: string,
        projectId: string
    ): Promise<FetchProjectRolesResponse>;

    getSignupAccountOrgDetails(
        request: GetSignupAccountOrgDetailsRequest
    ): Promise<GetSignupAccountOrgDetailsResponse>;

    asyncNewSiteActivation(
        request: AsyncNewSiteActivationRequest
    ): Promise<AsyncActivationStatusResponse>;

    asyncActivationStatus(
        request: AsyncActivationStatusRequest
    ): Promise<AsyncActivationStatusResponse>;

    getOrgServers(
        request: GetOrgServersPayload
    ): Promise<GetOrgServersResponse>;

    getL1Assessments(
        request: GetL1AssessmentsPayload
    ): Promise<GetL1AssessmentsResponse>;

    getMigrationPlanTotals(
        request: GetMigrationPlanTotalsPayload
    ): Promise<GetMigrationPlanTotalsResponse>;

    getAvailableSitesForFastshift(
        request: CloudSessionTokenCookieType
    ): Promise<AvailableProductsWACResponse>;

    getCspGraphqlRequest<T>(
        request: GetCustomerSupportRequestsPayload
    ): Promise<T>;

    getInstances(request: GetInstancesPayload): Promise<GetInstancesResponse>;

    getFastshiftUser(
        request: GetFastshiftUserRequest
    ): Promise<GetFastshiftUserResponse>;

    getFastshiftCustomerDomains(
        request: GetFastshiftCustomerDomainsRequest
    ): Promise<GetFastshiftCustomerDomainsResponse>;

    getUserBillingAdminTransactionAccounts(
        request: GetUserBillingAdminTransactionAccountsRequest
    ): Promise<GetUserBillingAdminTransactionAccounts>;
    getFastshiftCustomerDomainEngagement(
        request: GetCustomerDomainEngagementRequest
    ): Promise<GetCustomerDomainEngagementResponse>;

    getFastshiftCustomerDomain(
        request: GetFastshiftCustomerDomainRequest
    ): Promise<GetFastshiftCustomerDomainResponse>;

    getFastshiftSupportRequests(
        request: GetFastshiftSupportRequestsRequest
    ): Promise<GetFastshiftSupportRequestsResponse>;

    getFastshiftTopSupportRequests(
        request: GetFastshiftTopSupportRequestsRequest
    ): Promise<GetFastshiftTopSupportRequestsResponse>;

    fetchFastshiftPortalConsentRequest(
        request: FastshiftPortalConsentRequestPayload
    ): Promise<GetFastshiftPortalUserConsentResponse>;

    sendFastshiftPortalConsent(
        request: FastshiftPortalConsentSubmitPayload
    ): Promise<GetFastshiftPortalUserConsentResponse>;

    activateProductCrossflowPlan<T extends JobContext>(
        request: ActivateProductCrossflowRequest<T>
    ): Promise<ActivateProductCrossflowPlanResponse>;

    activateProductCrossflow<T extends JobContext>(
        request: ActivateProductCrossflowRequest<T>
    ): Promise<ActivateProductCrossflowResponse>;

    waitForActivateProductCrossflow(
        request: WaitForActivateProductCrossflowRequest
    ): Promise<WaitForActivateProductCrossflowResponse>;

    postMadlibContentGeneration(
        cloudId: string,
        request: MadlibContentGenerationRequest
    ): Promise<MadlibContentGenerationResponse>;

    postProjectCreate(
        payload: WizardCreateProjectPayload
    ): Promise<WizardCreateProjectResponse>;

    postContentGenerationForJiraFfl(
        request: JiraFFLContentGenerationRequest
    ): Promise<JiraFFLContentGenerationResponse>;

    getJiraFFLContentByCgsKey(
        cgsKey: string
    ): Promise<JiraFFLContentGenerationResponse>;

    postEmailInvite(
        cloudId: string,
        projectId: string,
        request: EmailInviteRequest
    ): Promise<EmailInviteResponse>;
}

export class BxpWebStargateClient implements IBxpWebStargateClient {
    public readonly baseUrl: string | undefined;

    public constructor({ baseUrl }: StargateClientOptions) {
        this.baseUrl = baseUrl;
    }

    public async fetch(
        urlPath: string,
        init?: RequestInit,
        removeBaseUrl?: boolean
    ): Promise<Response> {
        try {
            return fetch(
                removeBaseUrl ? urlPath : this.baseUrl + urlPath,
                init
            );
        } catch (error) {
            console.error("Fetch error:", error);
            return new Response(null, {
                status: 500,
                statusText: "Error"
            });
        }
    }

    public async me({
        cloudSessionTokenCookie
    }: MeOptions): Promise<UserProfile> {
        // note: not de-duping fetches like in asc-stargate
        const response = await this.fetch("/me", {
            headers: {
                ...mapCookieToHeaders(cloudSessionTokenCookie),
                "web-platform": "bxp-web-platform"
            },
            credentials: "include"
        });

        if (response.ok) {
            return mapMeResponseToUserProfile(await response.json());
        } else {
            throw new UnsuccessfulFetchError({
                response
            });
        }
    }

    // extended_profile defintion: https://bitbucket.org/atlassian/id-public-api-facade/src/b6dbac9d5f1b6951c88d93ebeda0c3b68283aa40/swagger.yaml#lines-2479:2493
    // DAC Docs: https://developer.atlassian.com/cloud/admin/user-management/rest/api-group-profile/#api-group-profile
    public async updateIdentityProfile({
        aaid,
        extendedProfile
    }: UpdateIdentityProfileParams): Promise<undefined> {
        return errorHandleResponse(
            await this.fetch(`/users/${aaid}/manage/profile`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    extended_profile: {
                        ...extendedProfile
                    }
                }),
                credentials: "include"
            })
        );
    }

    public async getSignupServerXsrfToken(): Promise<string> {
        return mapResponseToText(
            await this.fetch(`/bxp/signup/auth/xsrf`, {
                method: "GET",
                credentials: "include",
                headers: {
                    "web-platform": "bxp-web-platform",
                    "accept": "text/plain"
                }
            })
        );
    }

    /**
     * Get the authentication methods available to a given user.
     *
     * @see https://bxp-signup-server.us-west-2.prod.atl-paas.net/swagger/#/Identity/post_authentication_methods_available
     */
    public async checkAuthenticationMethodsAvailable({
        email,
        application = "wac",
        successRedirect
    }: CheckAuthenticationMethodsAvailableRequest): Promise<CheckAuthenticationMethodsAvailableResponse> {
        const xsrfToken = await this.getSignupServerXsrfToken();
        const atlCohortId = getAtlCohortIdFromCookie();

        return mapResponseToJson(
            await this.fetch(`/bxp/signup/authentication/methods-available`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "xsrf-token": xsrfToken,
                    "web-platform": "bxp-web-platform",
                    "X-Atl-Cohort-Id": atlCohortId,
                    "x-b3-sampled": "1"
                },
                credentials: "include",
                body: JSON.stringify({
                    email,
                    application,
                    continue: successRedirect
                })
            })
        );
    }

    public async availableProducts({
        cloudSessionTokenCookie,
        removeNonAdminSites,
        includeInvalidSites,
        includeInvalidBitbucketSite,
        makeS2sRequest,
        userContext,
        retryAttempted
    }: AisAvailableProductsOptions): Promise<AvailableProductsResponse> {
        let baseUrl = this.baseUrl
            ? `${this.baseUrl}/ais/available-products`
            : `/ais/available-products`;
        const headers: Record<string, string> = {
            ...(mapCookieToHeaders(cloudSessionTokenCookie) as Record<
                string,
                string
            >),
            "web-platform": "bxp-web-platform",
            "credentials": "include",
            "x-b3-sampled": "1"
        };

        if (makeS2sRequest && this.isSSR()) {
            //@ts-ignore
            const awsRegion: string = globalThis.ssrContext.awsRegion;

            // S2S calls only work if the downstream service exists in the same region that our tesseract runtime is in
            const downStreamServiceExistsInSameTesseractRuntimeRegion: boolean =
                BxpExpressServiceSupportedRegions.has(awsRegion);

            if (!downStreamServiceExistsInSameTesseractRuntimeRegion) {
                console.log(
                    "Downstream service does not exist in the same region as current tesseract region. Current tesseract region: " +
                        awsRegion
                );

                // Make non S2S call
                return await this.availableProducts({
                    cloudSessionTokenCookie,
                    removeNonAdminSites,
                    includeInvalidSites,
                    includeInvalidBitbucketSite,
                    makeS2sRequest: undefined
                });
            }
            headers["x-ssr-service-name"] = "bxp-express";
            // bxp-express service does not have any dev instances. So if it's dev -> we should point calls to stg
            const environmentType: string =
                //@ts-ignore
                globalThis.ssrContext.environmentType;

            if (environmentType === "dev") {
                // bxp-express service does not have any dev instances so when we're in dev, point to staging
                headers["x-ssr-service-env"] = "staging";
                headers["x-ssr-service-region"] = "us-east-1"; // Bxp-express service in staging only lives in us-east-1 or us-west-2
            }

            headers["user-context"] = userContext as string;
            baseUrl = "/ais/available-products";
        }

        let response;
        try {
            response = await this.fetch(
                baseUrl,
                {
                    headers
                },
                true
            );

            if (response.ok) {
                const result = await response.json();
                let sites = (result && result.sites) || [];

                if (removeNonAdminSites) {
                    sites = sites.filter(
                        (site: AisSitesType) => site.adminAccess
                    );
                }

                if (!includeInvalidSites) {
                    sites = sites.filter((site: AisSitesType) =>
                        isValidSite({
                            site,
                            includeInvalidBitbucketSite
                        })
                    );
                }

                return Promise.resolve({
                    sites,
                    isNewUser: !!(result && result.isNewUser)
                });
            } else {
                return Promise.resolve({
                    sites: [],
                    isNewUser: false,
                    callFailed: true
                });
            }
        } catch (error) {
            if (retryAttempted === false && makeS2sRequest === true) {
                console.log(
                    "There was an error making a call to available products for s2s call - retrying with non-s2s call"
                );
                return await this.availableProducts({
                    cloudSessionTokenCookie,
                    removeNonAdminSites,
                    includeInvalidSites,
                    includeInvalidBitbucketSite,
                    makeS2sRequest: undefined,
                    userContext: undefined, // This will automatically passed in as non-s2s calls will go through edge/stargate,
                    retryAttempted: true
                });
            }
            throw error;
        }
    }

    /**
     * The legacy get availableProducts call from IdentityClient
     */
    public async availableProductsLegacy({
        cloudSessionTokenCookie,
        includeInvalidSites,
        includeInvalidBitbucketSite
    }: AvailableProductsOptions): Promise<AvailableProductsLegacyResponse> {
        const reqUrl = `${this.baseUrl}/available-products/api/available-products`;
        const response = await fetch(reqUrl, {
            headers: {
                ...mapCookieToHeaders(cloudSessionTokenCookie),
                "web-platform": "bxp-web-platform"
            },
            credentials: "include"
        });

        if (response.ok) {
            const result = await response.json();
            let sites = (result && result.sites) || [];

            sites = _.remove(
                _.map(sites, (site: AvailableProductsType) => {
                    const products = _.map(
                        site.availableProducts,
                        (product) => {
                            return PRODUCT_KEY_MAP[product.productType];
                        }
                    ).filter((product) => product !== undefined);

                    if (!_.isEmpty(products)) {
                        return {
                            adminAccess: site.adminAccess,
                            cloudId: site.cloudId,
                            displayName: site.displayName,
                            url: site.url,
                            products
                        };
                    } else {
                        return null;
                    }
                }),
                (site) => site !== undefined && site !== null
            );

            if (!includeInvalidSites) {
                sites = sites.filter((site: SitesType) =>
                    isValidSite({
                        site,
                        includeInvalidBitbucketSite
                    })
                );
            }

            return Promise.resolve(sites);
        } else {
            throw new UnsuccessfulFetchError({
                response
            });
        }
    }

    public async activateProduct(
        request: ActivateProductRequest
    ): Promise<ActivateProductResponse> {
        const xsrfToken = await this.getSignupServerXsrfToken();
        const atlCohortId = getAtlCohortIdFromCookie();
        const headers = mapCookieToHeaders(request.cloudSessionTokenCookie);
        Object.assign(headers, {
            "Content-Type": "application/json",
            "xsrf-token": xsrfToken,
            "X-Atl-Cohort-Id": atlCohortId,
            "web-platform": "bxp-web-platform",
            "x-b3-sampled": "1",
            "recaptcha-site-key": getReCaptchaKey()
        });

        const upstreamResponse =
            await mapResponseToJson<ActivateProductUpstreamResponse>(
                await this.fetch(`/bxp/signup/activate-product`, {
                    method: "POST",
                    headers,
                    credentials: "include",
                    body: JSON.stringify(request)
                })
            );

        return upstreamResponse;
    }

    /**
     * Get a list of Bitbucket workspaces
     * @param nextUrl - override the request URL for pagination
     * bitbucket/user/permissions/workspaces?sort=workspace.slug
     */
    public async bitbucketWorkspaces({
        nextUrl,
        cloudSessionTokenCookie,
        urlQuery
    }: BitbucketWorkspaceOptions): Promise<BitbucketWorkspacesResponse> {
        let reqUrl = `${this.baseUrl}/bitbucket/user/permissions/workspaces`;
        if (nextUrl) {
            reqUrl = nextUrl;
        }
        if (urlQuery) {
            reqUrl = `${reqUrl}?${urlQuery}`;
        }

        const response = await fetch(reqUrl, {
            headers: {
                ...mapCookieToHeaders(cloudSessionTokenCookie),
                "web-platform": "bxp-web-platform"
            },
            credentials: "include"
        });

        if (response.ok) {
            return Promise.resolve(await response.json());
        } else {
            throw new UnsuccessfulFetchError({
                response
            });
        }
    }

    public async checkCloudNameAvailability({
        cloudName,
        cloudSessionTokenCookie
    }: CheckCloudNameAvailabilityRequest): Promise<CheckCloudNameAvailabilityResponse> {
        const headers = mapCookieToHeaders(cloudSessionTokenCookie);
        Object.assign(headers, {
            "Accept": "application/json",
            "Content-Type": "application/json",
            "web-platform": "bxp-web-platform"
        });
        return mapResponseToJson(
            await this.fetch(`/hostname/cloud/availability/check`, {
                method: "post",
                body: JSON.stringify({
                    cloudName
                }),
                headers,
                credentials: "include"
            })
        );
    }

    public async getAvailableSites(
        request: GetAvailableSitesRequest
    ): Promise<GetAvailableSitesResponse> {
        const { orderId, transactionAccount } = request;

        return mapResponseToJson(
            await fetch(
                `${this.baseUrl}/bxp/signup/v1/order/${orderId}/available-sites`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "web-platform": "bxp-web-platform",
                        "X-Transaction-Account": transactionAccount,
                        "x-b3-sampled": "1"
                    },
                    credentials: "include"
                }
            )
        );
    }

    public async getRecommendedSiteName(
        request: GetRecommendedSiteNameRequest
    ): Promise<GetRecommendedSiteNameResponse> {
        const xsrfToken = await this.getSignupServerXsrfToken();
        const atlCohortId = getAtlCohortIdFromCookie();
        const headers = mapCookieToHeaders(request.cloudSessionTokenCookie);
        Object.assign(headers, {
            "Content-Type": "application/json",
            "xsrf-token": xsrfToken,
            "X-Atl-Cohort-Id": atlCohortId,
            "web-platform": "bxp-web-platform"
        });

        const response =
            await mapResponseToJson<GetRecommendedSiteNameResponse>(
                await this.fetch("/bxp/signup/site/recommended-name", {
                    method: "POST",
                    headers,
                    credentials: "include",
                    body: JSON.stringify(request)
                })
            );

        return response;
    }

    public async getCloudStatus({
        cloudId,
        requestId
    }: GetCloudStatusOptions): Promise<GetCloudStatusResponse> {
        return mapResponseToJson(
            await this.fetch(`/cofs/${cloudId}/status/${requestId}`, {
                headers: {
                    "Content-Type": "application/json",
                    "X-Rate-Limit-Request-Id": requestId,
                    "web-platform": "bxp-web-platform"
                },
                credentials: "include"
            })
        );
    }

    public async getProductActivationStatus(
        request: ProductActivationStatusRequest
    ): Promise<ProductActivationStatusResponse> {
        const xsrfToken = await this.getSignupServerXsrfToken();

        return mapResponseToJson(
            await this.fetch(
                `/bxp/signup/activate/status/${request.provisioningRequestId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "xsrf-token": xsrfToken,
                        "web-platform": "bxp-web-platform",
                        "provision-polling-times": `${request.provisionPollingTimes}`,
                        "x-b3-sampled": "1"
                    },
                    credentials: "include"
                }
            )
        );
    }

    public async getUserPermissionsIsPermitted({
        permissionId,
        resourceId,
        dontRequirePrincipalInSite
    }: GetUserPermissionsIsPermittedRequest): Promise<GetUserPermissionsIsPermittedResponse> {
        return mapResponseToJson(
            await this.fetch(`/permissions/permitted`, {
                credentials: "include",
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    permissionId,
                    resourceId,
                    dontRequirePrincipalInSite
                })
            })
        );
    }

    public async getUserProfile({
        cloudSessionTokenCookie,
        productName,
        makeS2sRequest,
        userContext
    }: UserProfileOptions): Promise<UserProfileResponse> {
        const queryString = productName
            ? `?${new URLSearchParams({ productName })}`
            : "";

        let baseUrl = this.baseUrl
            ? `${this.baseUrl}/bxp/signup/user/profile${queryString}`
            : `/bxp/signup/user/profile${queryString}`;
        const headers: Record<string, string> = {
            ...(mapCookieToHeaders(cloudSessionTokenCookie) as Record<
                string,
                string
            >),
            "web-platform": "bxp-web-platform"
        };

        if (makeS2sRequest && this.isSSR()) {
            //@ts-ignore
            const awsRegion: string = globalThis.ssrContext.awsRegion;
            const environmentType: string =
                //@ts-ignore
                globalThis.ssrContext.environmentType;

            if (
                BxpSignupServerServiceSupportedRegions.has(awsRegion) &&
                environmentType !== "dev"
            ) {
                headers["x-ssr-service-name"] = "bxp-signup-server";
                headers["x-ssr-service-region"] = awsRegion;
                headers["user-context"] = userContext as string;
                baseUrl = `/user/profile${queryString}`;
            } else if (environmentType === "dev") {
                headers["x-ssr-service-name"] = "bxp-signup-server";
                headers["x-ssr-service-region"] = "us-west-2";
                headers["user-context"] = userContext as string;
                baseUrl = `/user/profile${queryString}`;
            }
        }

        return mapResponseToJson(
            await this.fetch(
                baseUrl,
                {
                    headers,
                    credentials: "include",
                    method: "GET"
                },
                true
            )
        );
    }

    public async postExpandFreeValidation(
        request: PostExpandFreeValidationRequest
    ): Promise<PostExpandFreeValidationResponse> {
        const { cloudId, productKeys } = request;

        return mapResponseToJson(
            await this.fetch(`/cloud/expand-free-validation`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "web-platform": "bxp-web-platform"
                },
                credentials: "include",
                body: JSON.stringify({
                    cloudId,
                    productKeys
                })
            })
        );
    }

    public async getActivationAuthorization(
        request: GetActivationAuthorizationRequest
    ): Promise<GetActivationAuthorizationResponse> {
        const { productKeys, productEdition, cloudId, siteName } = request;
        const atlCohortId = getAtlCohortIdFromCookie();

        return mapResponseToJson(
            await this.fetch(
                `/bxp/signup/activation-authorization?${new URLSearchParams({
                    productKeys: productKeys.join(","),
                    productEdition: productEdition,
                    cloudId: cloudId ?? "",
                    siteName
                })}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "X-Atl-Cohort-Id": atlCohortId,
                        "web-platform": "bxp-web-platform"
                    },
                    credentials: "include"
                }
            )
        );
    }

    public async getCcpHamsStatus(
        _getStatus: GetCcpHamsStatus
    ): Promise<GetCcpHamsResponse> {
        return { service: "CCP" };
    }

    public async getLicenceInformationStatus(
        request: GetLicenceInformationRequest
    ): Promise<GetLicenseInformationResponse> {
        const xsrfToken = await this.getSignupServerXsrfToken();

        return mapResponseToJson(
            await this.fetch(
                `/bxp/signup/cloud/${request.cloudId}/license-information`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "xsrf-token": xsrfToken,
                        "web-platform": "bxp-web-platform"
                    },
                    credentials: "include"
                }
            )
        );
    }

    public async postProductActivate(
        request: ProductActivateRequest
    ): Promise<ProductActivateResponse> {
        const xsrfToken = await this.getSignupServerXsrfToken();

        return mapResponseToJson(
            await this.fetch(`/bxp/signup/product/activate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "xsrf-token": xsrfToken,
                    "web-platform": "bxp-web-platform",
                    "x-b3-sampled": "1",
                    "recaptcha-site-key": getReCaptchaKey()
                },
                credentials: "include",
                body: JSON.stringify(request)
            })
        );
    }

    public async postCloudSignUp({
        payload
    }: PostCloudSignUpRequest): Promise<unknown> {
        return mapResponseToJson(
            await this.fetch(`/cloud/signup`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "web-platform": "bxp-web-platform"
                },
                body: JSON.stringify(payload),
                credentials: "include"
            })
        );
    }

    /**
     * For attaching a product to an existing site using COFS, xflow worked on the endpoint
     */
    public async postCOFSProductActivate(
        request: COFSProductActivateRequest
    ): Promise<COFSProductActivateResponse> {
        const xsrfToken = await this.getSignupServerXsrfToken();
        const { cloudId } = request;
        return mapResponseToJson(
            await fetch(`${this.baseUrl}/xflow/${cloudId}/activate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "xsrf-token": xsrfToken,
                    "web-platform": "bxp-web-platform"
                },
                credentials: "include",
                body: JSON.stringify(request)
            })
        );
    }

    public async user(): Promise<User | undefined> {
        const response = await this.fetch("/bxp/signup/user", {
            headers: {
                "web-platform": "bxp-web-platform"
            },
            credentials: "include"
        });
        if (response.ok) {
            const result = await response.json();
            return Promise.resolve(result);
        }
        return Promise.resolve(undefined);
    }

    public async userSignup(
        request: UserSignupRequest
    ): Promise<UserSignupResponse> {
        const xsrfToken = await this.getSignupServerXsrfToken();
        const atlCohortId = getAtlCohortIdFromCookie();

        return mapResponseToJson(
            await this.fetch(`/bxp/signup/identity/user-signup`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "xsrf-token": xsrfToken,
                    "X-Atl-Cohort-Id": atlCohortId,
                    "web-platform": "bxp-web-platform",
                    "x-b3-sampled": "1",
                    "recaptcha-site-key": getReCaptchaKey()
                },
                credentials: "include",
                body: JSON.stringify({ ...request })
            })
        );
    }

    public async getConsentConfig(): Promise<LocaleType> {
        const xsrfToken = await this.getSignupServerXsrfToken();
        return mapResponseToJson(
            await this.fetch(`/preferencesV2/consent-config`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "web-platform": "bxp-web-platform",
                    "xsrf-token": xsrfToken
                },
                credentials: "include"
            })
        );
    }

    public async updateConsentPreferences(
        postData: UpdateConsentPreferencesRequest
    ): Promise<unknown> {
        const xsrfToken = await this.getSignupServerXsrfToken();
        const preferencesV2Update = await this.fetch(`/preferencesV2/update`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "xsrf-token": xsrfToken
            },
            credentials: "include",
            body: JSON.stringify(postData)
        });
        return preferencesV2Update.status;
    }

    public async getInviteLink(
        postData: GetInviteLinkRequest
    ): Promise<GetInviteLinkResponse> {
        return this.fetch(
            `/bxp/signup/experimental/confluence/product/onboarding/invitation_url`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "web-platform": "bxp-web-platform"
                },
                credentials: "include",

                body: JSON.stringify(postData)
            }
        ).then((response) => response.json());
    }

    public async submitConfluenceOnboardingSelections(
        postData: SubmitConfluenceOnboardingSelectionsRequest
    ): Promise<SubmitConfluenceOnboardingSelectionsResponse> {
        return this.fetch(
            `/bxp/signup/experimental/confluence/product/onboarding`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "web-platform": "bxp-web-platform"
                },
                credentials: "include",
                body: JSON.stringify(postData)
            }
        ).then((response) => response.json());
    }

    public isSSR() {
        return typeof window === "undefined";
    }

    public async postUserSegmentationViewAttempt(): Promise<unknown> {
        const response = await this.fetch(
            "/user-seg-redirector/v1/view-attempt",
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "web-platform": "bxp-web-platform"
                },
                credentials: "include"
            }
        );

        if (response.ok) {
            const result = await response.json();
            return result;
        } else {
            return { success: false };
        }
    }

    public async getUserSegmentationRedirectAdvice(
        params: GetUserSegmentationRedirectAdviceRequest
    ): Promise<GetUserSegmentationRedirectAdviceResponse> {
        const response = await this.fetch(
            `/user-seg-redirector/v1/redirect-advice?${
                params.userId
            }&${JSON.stringify(params.continue)}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "web-platform": "bxp-web-platform"
                },
                credentials: "include"
            }
        );

        if (response.ok) {
            return await response.json();
        } else {
            return { redirect: true, cloudId: "", duration: 0 };
        }
    }

    public async postJiraProjectCreate(
        request: JiraProjectCreateRequest
    ): Promise<JiraProjectCreateResponse> {
        const response = await this.fetch(
            "/bxp/signup/experimental/jira/project/create",
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                credentials: "include",
                body: JSON.stringify(request)
            }
        );

        if (response.ok) {
            const result = await response.json();
            return result;
        }

        let result;
        try {
            result = await response.json();
        } catch {
            result = { error: "Body Empty" };
        }

        switch (response.status) {
            case 400:
                if (result.error === "") {
                    throw new Error(
                        JiraProjectCreateErrors.noProjectCreateAccess
                    );
                    break;
                }

                if (result.error.includes("uses this project key")) {
                    throw new Error(
                        JiraProjectCreateErrors.projectKeyCollision
                    );
                    break;
                }

                if (
                    result.error.includes(
                        "project with that name already exists"
                    )
                ) {
                    throw new Error(
                        JiraProjectCreateErrors.projectNameCollision
                    );
                    break;
                }

                throw new Error(JiraProjectCreateErrors.badRequest);
                break;
            case 403:
                throw new Error(JiraProjectCreateErrors.noSiteAccess);
                break;
            case 500:
            default:
                throw new Error(JiraProjectCreateErrors.badGateway);
                break;
        }
    }

    public async postRequestAccessUserResource(
        params: PostRequestAccessUserResourceRequest
    ): Promise<PostRequestAccessUserResourceResponse> {
        const response = await this.fetch("/bxp/signup/product/join", {
            method: "POST",
            headers: {
                ...mapCookieToHeaders(params.cloudSessionTokenCookie),
                "web-platform": "bxp-web-platform",
                "Content-Type": "application/json"
            },
            credentials: "include",
            body: JSON.stringify(params.requestBody)
        });
        return response.json();
    }

    /* istanbul ignore next */
    public async getAccountOrgs({
        cloudSessionTokenCookie,
        includeVortexMode,
        skipNames,
        getOrgType
    }: GetAccountOrgsRequest): Promise<GetAccountOrgsResponse> {
        const response = await this.fetch(
            `/bxp/signup/account/orgs?${new URLSearchParams({
                skipNames: skipNames ? "true" : "false",
                getOrgType: getOrgType ? "true" : "false",
                ...(includeVortexMode ? { includeVortexMode: "true" } : {})
            })}`,
            {
                headers: {
                    ...mapCookieToHeaders(cloudSessionTokenCookie),
                    "web-platform": "bxp-web-platform",
                    "x-b3-sampled": "1"
                },
                credentials: "include"
            }
        );
        if (response.ok) {
            const result = await response.json();
            return { success: true, accountOrgs: result };
        } else {
            return { success: false };
        }
    }

    public async getAdminhubAccountOrgs({
        cloudSessionTokenCookie
    }: GetAdminhubAccountOrgsRequest): Promise<GetAdminhubAccountOrgsResponse> {
        const response = await this.fetch(`/adminhub/organization/my3`, {
            headers: {
                ...mapCookieToHeaders(cloudSessionTokenCookie),
                "web-platform": "bxp-web-platform"
            },
            credentials: "include"
        });
        if (response.ok) {
            const result = await response.json();
            return { success: true, accountOrgs: result };
        } else {
            sendFastshiftSentryAlert(
                "Error on fetching adminhub account orgs",
                response.status,
                response.statusText,
                response.url
            );
            return { success: false };
        }
    }

    public async getAdminhubOrgDetails({
        cloudSessionTokenCookie,
        orgId
    }: GetAdminhubOrgDetailsRequest): Promise<GetAdminhubOrgDetailsResponse> {
        const response = await this.fetch(`/adminhub/organization/${orgId}`, {
            headers: {
                ...mapCookieToHeaders(cloudSessionTokenCookie),
                "web-platform": "bxp-web-platform"
            },
            credentials: "include"
        });
        if (response.ok) {
            const result = await response.json();
            return { success: true, organization: result };
        } else {
            return { success: false };
        }
    }

    public async getSignupAccountOrgDetails({
        cloudSessionTokenCookie,
        orgId,
        offeringIds
    }: GetSignupAccountOrgDetailsRequest): Promise<GetSignupAccountOrgDetailsResponse> {
        try {
            const response = await this.fetch(
                `/bxp/signup/v1/account/org?${new URLSearchParams({
                    orgId,
                    ...(offeringIds && offeringIds.length > 0
                        ? { offeringIds: offeringIds.join(",") }
                        : {})
                })}`,
                {
                    headers: {
                        ...mapCookieToHeaders(cloudSessionTokenCookie),
                        "web-platform": "bxp-web-platform",
                        "x-b3-sampled": "1"
                    },
                    credentials: "include"
                }
            );
            if (response.ok) {
                const result = await response.json();
                return {
                    success: true,
                    organization: { id: orgId, ...result }
                };
            } else {
                return { success: false };
            }
        } catch (_error) {
            return { success: false };
        }
    }

    public async activateDirectBuyOnOrg(
        request: ActivateDirectBuyOnOrgRequest
    ): Promise<ActivateDirectBuyOnOrgResponse> {
        const xsrfToken = await this.getSignupServerXsrfToken();
        const atlCohortId = getAtlCohortIdFromCookie();

        const headers = mapCookieToHeaders(request.cloudSessionTokenCookie);
        Object.assign(headers, {
            "Content-Type": "application/json",
            "xsrf-token": xsrfToken,
            "X-Atl-Cohort-Id": atlCohortId,
            "web-platform": "bxp-web-platform",
            "x-b3-sampled": "1"
        });

        return mapResponseToJson(
            await fetch(`${this.baseUrl}/bxp/signup/activation`, {
                method: "POST",
                headers,
                credentials: "include",
                body: JSON.stringify({
                    timezone: request.timezone,
                    region: request.region,
                    orderId: request.orderId,
                    orgId: request.orgId,
                    transactionAccountId: request.transactionAccountId
                })
            })
        );
    }

    public async getAccountProducts({
        cloudSessionTokenCookie
    }: GetAccountProductsRequest): Promise<GetAccountProductsResponse> {
        const response = await this.fetch("/bxp/signup/account/products", {
            headers: {
                ...mapCookieToHeaders(cloudSessionTokenCookie),
                "web-platform": "bxp-web-platform"
            },
            credentials: "include"
        });
        if (response.ok) {
            const result = await response.json();
            return { success: true, accountProducts: result };
        } else {
            return { success: false };
        }
    }

    public async getActivationType({
        orderId,
        transactionAccount
    }: GetActivationRequest): Promise<GetActivationResponse> {
        const response = await this.fetch(
            `/bxp/signup/activation/type?orderId=${orderId}&transactionAccountId=${transactionAccount}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "web-platform": "bxp-web-platform",
                    "x-b3-sampled": "1"
                },
                credentials: "include"
            }
        );

        if (response.ok) {
            return response.json();
        } else {
            throw new UnsuccessfulFetchError({
                response
            });
        }
    }

    /* istanbul ignore next */
    public async cofsCreateOrg(
        request: CreateOrgRequest
    ): Promise<CreateOrgResponse> {
        const xsrfToken = await this.getSignupServerXsrfToken();
        const atlCohortId = getAtlCohortIdFromCookie();

        return mapResponseToJson(
            await fetch(`${this.baseUrl}/bxp/signup/cofs-create-organization`, {
                method: "POST",
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                    "xsrf-token": xsrfToken,
                    "X-Atl-Cohort-Id": atlCohortId,
                    "web-platform": request.webPlatform || "no-platform"
                },
                credentials: "include",
                body: JSON.stringify(request)
            })
        );
    }

    /* istanbul ignore next */
    public async bitbucketValidateWorkspaceSlug({
        workspaceSlug
    }: BitbucketValidateWorkspaceSlugRequest): Promise<BitbucketValidateWorkspaceSlugResponse> {
        return mapResponseToJson<BitbucketValidateWorkspaceSlugResponse>(
            await fetch(
                `${this.baseUrl}/bitbucket/api/internal/workspace/slug/validate`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        workspace_slug: workspaceSlug
                    })
                }
            )
        );
    }

    public async getCloudProductInfo(
        request: GetCloudProductInfoRequest
    ): Promise<CloudProductInfo> {
        return mapResponseToJson<CloudProductInfo>(
            await fetch(
                `${this.baseUrl}/bxp/signup/v1/cloud/${request.cloudId}/product-information`,
                {
                    headers: {
                        "Content-Type": "application/json"
                    },
                    credentials: "include"
                }
            )
        );
    }

    public async activateDirectBuyProduct(
        request: ActivateDirectBuyProductRequest
    ): Promise<ActivateDirectBuyProductResponse> {
        const xsrfToken = await this.getSignupServerXsrfToken();

        return mapResponseToJson(
            await fetch(
                `${this.baseUrl}/bxp/signup/v1/order/${request.orderId}/activation`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "xsrf-token": xsrfToken,
                        "X-Transaction-Account": request.transactionAccountId,
                        "web-platform": request.webPlatform || "no-platform",
                        "x-b3-sampled": "1"
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        timezone: request.timezone,
                        region: request.region,
                        site: request.site,
                        ...(request.orgId ? { orgId: request.orgId } : {})
                    })
                }
            )
        );
    }

    public async putThemePreference({
        cloudId,
        product,
        theme
    }: ThemePreferencePayload): Promise<boolean> {
        const updateThemePreference = await this.fetch(
            `/ex/jira/${cloudId}/rest/api/3/mypreferences?key=${product}.user.theme.preference`,
            {
                method: "PUT",
                headers: {
                    "web-platform": "bxp-web-platform",
                    "Content-Type": "application/json"
                },
                credentials: "include",
                body: JSON.stringify({ value: theme })
            }
        );
        if (!updateThemePreference.ok) {
            throw new Error(
                `Failed to update theme preference: ${updateThemePreference.statusText}`
            );
            return false;
        }

        return true;
    }

    public async sampleProjectCreate({
        cloudId,
        template,
        teamType,
        issueTypes,
        statusNames,
        projectName,
        projectKey,
        customDetail
    }: CreateSampleProjectPayload): Promise<CreateSampleProjectResponse> {
        const MAX_RETRIES = 5;
        const BACKOFF_DELAY = 1000; // Start with 1-second delay

        const postRequest = async (): Promise<CreateSampleProjectResponse> => {
            const createProjectResponse = await this.fetch(
                `/ex/jira/${cloudId}/rest/onboarding/v1/projects`,
                {
                    method: "POST",
                    headers: {
                        "web-platform": "bxp-web-platform",
                        "Content-Type": "application/json"
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        issueTypes,
                        statusNames,
                        projectName,
                        projectKey,
                        template,
                        teamType,
                        customDetail
                    })
                }
            );
            if (!createProjectResponse.ok) {
                throw new Error(
                    `Failed to create project: ${createProjectResponse.statusText}`
                );
            }
            const { taskId } = await createProjectResponse.json();
            const taskResponse = await this.pollTaskUntilComplete(
                taskId,
                cloudId
            );
            return {
                projectId: taskResponse.result.projectId || ""
            };
        };

        let retryCount = 0;
        while (retryCount < MAX_RETRIES) {
            try {
                return await postRequest();
            } catch (error) {
                // task failures should not attempt a project creation again
                if (error instanceof TaskError) {
                    console.error(
                        `Task failed with status: ${error.status}, message: ${error.taskMessage}`
                    );
                    throw error;
                }

                retryCount++;

                if (retryCount >= MAX_RETRIES) {
                    console.error(
                        `Max retries reached. Unable to create the project.`
                    );
                    throw new Error(
                        `Failed to create project after ${MAX_RETRIES} attempts.`
                    );
                }

                // Exponential backoff
                const delay = BACKOFF_DELAY * Math.pow(2, retryCount - 1);

                console.warn(
                    `Retrying sample project creation (attempt ${retryCount} of ${MAX_RETRIES}) in ${delay}ms...`
                );

                // Wait before retrying
                await this.delay(delay);
            }
        }

        // This should never be reached because the loop either succeeds or throws
        throw new Error("Unexpected error in project creation.");
    }

    // Utility function to delay for a specified number of milliseconds
    private async delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    public async wizardProjectCreate(
        payload: WizardCreateProjectPayload
    ): Promise<WizardCreateProjectResponse> {
        const createProjectResponse = await this.fetch(
            `/ex/jira/${payload.cloudId}/rest/onboarding/v1/projects`,
            {
                method: "POST",
                headers: {
                    "web-platform": "bxp-web-platform",
                    "Content-Type": "application/json"
                },
                credentials: "include",
                body: JSON.stringify({
                    issueTypes: payload.issueTypes,
                    statusNames: payload.statusNames,
                    projectName: payload.projectName,
                    projectKey: payload.projectKey,
                    template: payload.template,
                    teamType: payload.teamType,
                    customDetail: payload.customDetail,
                    sampleIssues: payload.sampleIssues
                })
            }
        );
        if (!createProjectResponse.ok) {
            const errorMessage = await getErrorMessage(createProjectResponse);
            throw new Error(
                `Failed to create project: ${createProjectResponse.statusText}, ${createProjectResponse.status}, ${errorMessage}`
            );
        }
        const { taskId } = await createProjectResponse.json();

        return {
            taskId
        };
    }

    public async postProjectCreate(
        payload: WizardCreateProjectPayload
    ): Promise<WizardCreateProjectResponse> {
        const createProjectResponse = await this.fetch(
            `/ex/jira/${payload.cloudId}/rest/onboarding/v1/projects`,
            {
                method: "POST",
                headers: {
                    "web-platform": "bxp-web-platform",
                    "Content-Type": "application/json"
                },
                credentials: "include",
                body: JSON.stringify({
                    issueTypes: payload.issueTypes,
                    statusNames: payload.statusNames,
                    projectName: payload.projectName,
                    projectKey: payload.projectKey,
                    template: payload.template,
                    teamType: payload.teamType,
                    customDetail: payload.customDetail,
                    sampleIssues: payload.sampleIssues
                })
            }
        );
        if (!createProjectResponse.ok) {
            throw new ProjectCreateError(createProjectResponse);
        }
        const { taskId } = await createProjectResponse.json();

        return {
            taskId
        };
    }

    public async putUserProperty<T>({
        tenantId,
        propertyKey,
        data
    }: {
        tenantId: string;
        propertyKey: string;
        data: T;
    }): Promise<boolean> {
        const accountId = Cookies.get("__aid_user_id");

        const response = await this.fetch(
            `/ex/jira/${tenantId}/rest/api/3/user/properties/${propertyKey}?accountId=${accountId}`,
            {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json"
                },
                credentials: "include",
                body: JSON.stringify(data)
            }
        );

        if (!response.ok) {
            throw new Error(`Failed to create project: ${response.statusText}`);
        }

        return response.ok;
    }

    public async putSampleProjectUserProperties({
        projectId,
        tenantId
    }: PutSampleProjectUserPropertiesPayload): Promise<boolean> {
        const propertyKey = `jira.onboarding.view.sample.project.cta.tenant.${tenantId}`;

        return this.putUserProperty({
            tenantId,
            propertyKey,
            data: {
                status: "ENABLED",
                timestamp: Date.now(),
                projectIds: [projectId]
            }
        });
    }

    public async pollTaskUntilComplete(
        taskId: string,
        cloudId: string,
        TIMEOUT: number = POLL_TASK_TIMEOUT
    ): Promise<FetchTaskResponse> {
        const abortController = new AbortController();
        const timeoutId = setTimeout(() => abortController.abort(), TIMEOUT);
        const pollTask = async (): Promise<FetchTaskResponse> => {
            try {
                const response = await this.fetchTask(taskId, cloudId);
                switch (response.status) {
                    case "CANCEL_REQUESTED":
                    case "CANCELLED":
                    case "DEAD":
                    case "FAILED":
                        throw new TaskError("Task failed", {
                            id: taskId,
                            status: response.status,
                            taskMessage: response.message
                        });
                    case "ENQUEUED":
                    case "RUNNING":
                        await new Promise((resolve) =>
                            setTimeout(resolve, 1000)
                        );
                        return pollTask();
                    case "COMPLETE":
                        clearTimeout(timeoutId);
                        return response;
                    default:
                        throw new TaskError("Unknown task status", {
                            id: taskId,
                            status: response.status,
                            taskMessage: response.message
                        });
                }
            } catch (error) {
                if (abortController.signal.aborted) {
                    throw new Error("Task polling timed out");
                }
                throw error;
            }
        };
        return pollTask();
    }
    private async fetchTask(
        taskId: string,
        cloudId: string
    ): Promise<FetchTaskResponse> {
        return this.fetch(`/ex/jira/${cloudId}/rest/api/3/task/${taskId}`, {
            method: "GET",
            credentials: "include"
        }).then((response) => response.json());
    }

    public async fetchProjectRoles(
        cloudId: string,
        projectId: string
    ): Promise<FetchProjectRolesResponse> {
        return this.fetch(
            `/ex/jira/${cloudId}/rest/api/3/project/${projectId}/role`,
            {
                method: "GET",
                credentials: "include"
            }
        ).then((response) => response.json());
    }

    /* istanbul ignore next */
    public async asyncNewSiteActivation(
        request: AsyncNewSiteActivationRequest
    ): Promise<AsyncActivationStatusResponse> {
        const xsrfToken = await this.getSignupServerXsrfToken();
        const atlCohortId = getAtlCohortIdFromCookie();
        const headers = {
            "Content-Type": "application/json",
            "xsrf-token": xsrfToken,
            "X-Atl-Cohort-Id": atlCohortId,
            "web-platform": "bxp-web-platform",
            "recaptcha-site-key": getReCaptchaKey()
        };

        return mapResponseToJson<AsyncActivationStatusResponse>(
            await this.fetch(
                `/bxp/signup/v1/activation-orchestration/new-site`,
                {
                    method: "POST",
                    credentials: "include",
                    body: JSON.stringify(request),
                    headers
                }
            )
        );
    }

    /* istanbul ignore next */
    public async asyncActivationStatus(
        request: AsyncActivationStatusRequest
    ): Promise<AsyncActivationStatusResponse> {
        const xsrfToken = await this.getSignupServerXsrfToken();
        const headers = {
            "Content-Type": "application/json",
            "xsrf-token": xsrfToken,
            "web-platform": "bxp-web-platform"
        };

        return mapResponseToJson<AsyncActivationStatusResponse>(
            await this.fetch(
                `/bxp/signup/v1/activation-orchestration/status?workflowId=${request.workflowId}`,
                {
                    method: "GET",
                    credentials: "include",
                    headers
                }
            )
        );
    }

    public async getOrgServers({
        orgId,
        cloudSessionTokenCookie
    }: GetOrgServersPayload): Promise<GetOrgServersResponse> {
        const response = await this.fetch(
            `/migrations/metadata/v1/org/${orgId}/server`,
            {
                method: "GET",
                headers: {
                    ...mapCookieToHeaders(cloudSessionTokenCookie),
                    "web-platform": "bxp-web-platform"
                },
                credentials: "include"
            }
        );

        if (response.ok) {
            const result = await response.json();

            if (result.data === undefined) {
                return Promise.resolve({ data: [] });
            }

            return Promise.resolve({ data: result.data });
        } else {
            sendFastshiftSentryAlert(
                "Error on fetching org servers",
                response.status,
                response.statusText,
                response.url
            );

            throw new UnsuccessfulFetchError({
                message: "Error fetching org servers",
                response
            });
        }
    }

    public async getL1Assessments({
        orgId,
        cloudSessionTokenCookie
    }: GetL1AssessmentsPayload): Promise<GetL1AssessmentsResponse> {
        const response = await this.fetch(
            `/mcc/v2/orgs/${orgId}/l1-assessments/`,
            {
                method: "GET",
                headers: {
                    ...mapCookieToHeaders(cloudSessionTokenCookie),
                    "web-platform": "bxp-web-platform"
                },
                credentials: "include"
            }
        );

        if (!response.ok) {
            sendFastshiftSentryAlert(
                "Error on fetching l1 assessments",
                response.status,
                response.statusText,
                response.url
            );
        }

        return mapResponseToJson<GetL1AssessmentsResponse>(response);
    }

    public async getMigrationPlanTotals({
        orgId,
        cloudSessionTokenCookie
    }: GetMigrationPlanTotalsPayload): Promise<GetMigrationPlanTotalsResponse> {
        const response = await this.fetch(
            `/migrations/planning/v1/org/${orgId}/totals/scope`,
            {
                method: "GET",
                headers: {
                    ...mapCookieToHeaders(cloudSessionTokenCookie),
                    "web-platform": "bxp-web-platform"
                },
                credentials: "include"
            }
        );

        if (!response.ok) {
            sendFastshiftSentryAlert(
                "Error on fetching migration plan totals",
                response.status,
                response.statusText,
                response.url
            );
        }

        return mapResponseToJson<GetMigrationPlanTotalsResponse>(response);
    }

    public async getAvailableSitesForFastshift({
        cloudSessionTokenCookie
    }: CloudSessionTokenCookieType): Promise<AvailableProductsWACResponse> {
        const response = await this.fetch(
            `/app-switcher/api/available-products?product=WAC`,
            {
                method: "GET",
                headers: {
                    ...mapCookieToHeaders(cloudSessionTokenCookie),
                    "web-platform": "bxp-web-platform"
                },
                credentials: "include"
            }
        );

        if (!response.ok) {
            sendFastshiftSentryAlert(
                "Error on fetching available products",
                response.status,
                response.statusText,
                response.url
            );
        }

        return mapResponseToJson<AvailableProductsWACResponse>(response);
    }

    public async getCspGraphqlRequest<T>({
        request,
        cloudSessionTokenCookie
    }: GetCustomerSupportRequestsPayload): Promise<T> {
        const response = await this.fetch(`/customer-support/csp/graphql`, {
            method: "POST",
            headers: {
                ...mapCookieToHeaders(cloudSessionTokenCookie),
                "web-platform": "bxp-web-platform",
                "Content-Type": "application/json"
            },
            credentials: "include",
            body: request
        });

        return mapResponseToJson<T>(response);
    }

    public async getInstances({
        orgId,
        cloudSessionTokenCookie
    }: GetInstancesPayload): Promise<GetInstancesResponse> {
        const response = await this.fetch(
            `/mcc/v1/orgs/${orgId}/discover/instances`,
            {
                method: "GET",
                headers: {
                    "web-platform": "bxp-web-platform",
                    ...mapCookieToHeaders(cloudSessionTokenCookie)
                },
                credentials: "include"
            }
        );

        if (!response.ok) {
            sendFastshiftSentryAlert(
                "Error on fetching MMA instances",
                response.status,
                response.statusText,
                response.url
            );
        }

        return mapResponseToJson<GetInstancesResponse>(response);
    }

    public async getFastshiftUser(
        request: GetFastshiftUserRequest
    ): Promise<GetFastshiftUserResponse> {
        const response = await this.fetch(`/fastshift-portal/v1/user`, {
            method: "GET",
            headers: {
                "web-platform": "bxp-web-platform",
                ...mapCookieToHeaders(request.cloudSessionTokenCookie)
            },
            credentials: "include"
        });

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error(
                    FastshiftUserDetailsExpectedError.NO_ELIGIBLE_LICENSE
                );
            } else if (response.status === 401) {
                throw new Error(FastshiftUserDetailsExpectedError.NO_ACCESS);
            } else {
                sendFastshiftSentryAlert(
                    "Error on fetching user details",
                    response.status,
                    response.statusText,
                    response.url
                );
            }
        }

        return mapResponseToJson<GetFastshiftUserResponse>(response);
    }

    public async getFastshiftCustomerDomains(
        request: GetFastshiftCustomerDomainsRequest
    ): Promise<GetFastshiftCustomerDomainsResponse> {
        const params = new URLSearchParams({
            page: request.page.toString(),
            size: request.size.toString()
        });

        if (request.sortBy) {
            params.append("sortBy", request.sortBy);
        }

        if (request.sortDirection) {
            params.append("sortDirection", String(request.sortDirection));
        }

        if (request.searchTerm) {
            params.append("queryTerm", request.searchTerm);
        }

        if (request.filters["status"]) {
            params.append("status", request.filters["status"]);
        }

        const response = await this.fetch(
            `/fastshift-portal/v1/user/domains?${params.toString()}`,
            {
                method: "GET",
                headers: {
                    "web-platform": "bxp-web-platform",
                    ...mapCookieToHeaders(request.cloudSessionTokenCookie)
                },
                credentials: "include"
            }
        );

        if (!response.ok) {
            sendFastshiftSentryAlert(
                "Error on fetching customer domains list",
                response.status,
                response.statusText,
                response.url
            );
        }

        return mapResponseToJson<GetFastshiftCustomerDomainsResponse>(response);
    }

    public async getUserBillingAdminTransactionAccounts({
        cloudSessionTokenCookie
    }: GetUserBillingAdminTransactionAccountsRequest) {
        const headers: Record<string, string> = {
            ...(mapCookieToHeaders(cloudSessionTokenCookie) as Record<
                string,
                string
            >),
            "web-platform": "bxp-web-platform"
        };

        const response = await this.fetch("/ccp/api/v1/transaction-accounts", {
            method: "GET",
            headers,
            credentials: "include"
        });

        return mapResponseToJson<GetUserBillingAdminTransactionAccounts>(
            response
        );
    }

    public async getFastshiftCustomerDomainEngagement(
        request: GetCustomerDomainEngagementRequest
    ): Promise<GetCustomerDomainEngagementResponse> {
        const response = await this.fetch(
            `/fastshift-portal/v1/engagement/${request.customerDomainId}`,
            {
                method: "GET",
                headers: {
                    "web-platform": "bxp-web-platform",
                    ...mapCookieToHeaders(request.cloudSessionTokenCookie)
                },
                credentials: "include"
            }
        );

        if (!response.ok) {
            if (response.status === 401) {
                throw new Error(
                    CustomerDomainEngagementExpectedError.NO_ACCESS
                );
            } else if (response.status === 403) {
                throw new Error(
                    CustomerDomainEngagementExpectedError.USER_NOT_PROVISIONED
                );
            } else if (response.status === 404) {
                throw new Error(
                    CustomerDomainEngagementExpectedError.NO_ENGAGEMENT
                );
            } else {
                sendFastshiftSentryAlert(
                    "Error on fetching customer domain engagement details",
                    response.status,
                    response.statusText,
                    response.url
                );
            }
        }

        return mapResponseToJson<GetCustomerDomainEngagementResponse>(response);
    }

    public async getFastshiftCustomerDomain(
        request: GetFastshiftCustomerDomainRequest
    ): Promise<GetFastshiftCustomerDomainResponse> {
        const response = await this.fetch(
            `/fastshift-portal/v1/user/domain/${request.customerDomainId}`,
            {
                method: "GET",
                headers: {
                    "web-platform": "bxp-web-platform",
                    ...mapCookieToHeaders(request.cloudSessionTokenCookie)
                },
                credentials: "include"
            }
        );

        if (!response.ok) {
            if (response.status === 401) {
                throw new Error(CustomerDomainExpectedError.NO_ACCESS);
            } else if (response.status === 404) {
                throw new Error(CustomerDomainExpectedError.NOT_FOUND);
            } else {
                sendFastshiftSentryAlert(
                    "Error on fetching customer domain details",
                    response.status,
                    response.statusText,
                    response.url
                );
            }
        }

        return mapResponseToJson<GetFastshiftCustomerDomainResponse>(response);
    }

    public async getFastshiftSupportRequests(
        request: GetFastshiftSupportRequestsRequest
    ): Promise<GetFastshiftSupportRequestsResponse> {
        const queryString = new URLSearchParams(request.queryParams).toString();

        const response = await this.fetch(
            `/fastshift-portal/v1/support-tickets/${request.customerDomainId}${
                queryString ? `?${queryString}` : ""
            }`,
            {
                method: "GET",
                headers: {
                    "web-platform": "bxp-web-platform",
                    ...mapCookieToHeaders(request.cloudSessionTokenCookie)
                },
                credentials: "include"
            }
        );

        if (!response.ok) {
            sendFastshiftSentryAlert(
                "Error on fetching support requests",
                response.status,
                response.statusText,
                response.url
            );
        }

        return mapResponseToJson<GetFastshiftSupportRequestsResponse>(response);
    }

    public async getFastshiftTopSupportRequests(
        request: GetFastshiftTopSupportRequestsRequest
    ): Promise<GetFastshiftTopSupportRequestsResponse> {
        const queryString = new URLSearchParams(request.queryParams).toString();

        const response = await this.fetch(
            `/fastshift-portal/v1/support-tickets/${request.domainId}/top${
                queryString ? `?${queryString}` : ""
            }`,
            {
                method: "GET",
                headers: {
                    "web-platform": "bxp-web-platform",
                    ...mapCookieToHeaders(request.cloudSessionTokenCookie)
                },
                credentials: "include"
            }
        );

        if (!response.ok) {
            sendFastshiftSentryAlert(
                "Error on fetching top support requests",
                response.status,
                response.statusText,
                response.url
            );
        }

        return mapResponseToJson<GetFastshiftTopSupportRequestsResponse>(
            response
        );
    }

    // fetch whether the user has consented already
    public async fetchFastshiftPortalConsentRequest(
        request: FastshiftPortalConsentRequestPayload
    ): Promise<GetFastshiftPortalUserConsentResponse> {
        const response = await this.fetch(
            "/fastshift-portal/v1/consent/portal/fetch",
            {
                method: "POST",
                headers: {
                    "web-platform": "bxp-web-platform",
                    ...mapCookieToHeaders(request.cloudSessionTokenCookie),
                    "Content-Type": "application/json"
                },
                credentials: "include",
                body: JSON.stringify(request.requestBody)
            }
        );

        if (!response.ok) {
            sendFastshiftSentryAlert(
                "Error on fetching fastshift portal consent status",
                response.status,
                response.statusText,
                response.url
            );
            throw new Error(
                "Error on fetching fastshift portal consent status"
            );
        }

        return mapResponseToJson<GetFastshiftPortalUserConsentResponse>(
            response
        );
    }

    // record the user's consent
    public async sendFastshiftPortalConsent(
        request: FastshiftPortalConsentRequestPayload
    ): Promise<GetFastshiftPortalUserConsentResponse> {
        const response = await this.fetch(
            "/fastshift-portal/v1/consent/portal",
            {
                method: "POST",
                headers: {
                    "web-platform": "bxp-web-platform",
                    ...mapCookieToHeaders(request.cloudSessionTokenCookie),
                    "Content-Type": "application/json"
                },
                credentials: "include",
                body: JSON.stringify(request.requestBody)
            }
        );

        if (!response.ok) {
            sendFastshiftSentryAlert(
                "Error on sending fastshift portal consent acceptance",
                response.status,
                response.statusText,
                response.url
            );
            throw new Error(
                "Error on sending fastshift portal consent acceptance"
            );
        }

        return mapResponseToJson<GetFastshiftPortalUserConsentResponse>(
            response
        );
    }

    public async activateProductCrossflowPlan<T extends JobContext>(
        request: ActivateProductCrossflowRequest<T>
    ): Promise<ActivateProductCrossflowPlanResponse> {
        const provisioningApiUrl = `/xflow/provision/cloud/${request.cloudId}/product/${request.productKey}/job/${request.jobName}/plan`;

        const response = await this.fetch(provisioningApiUrl, {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(request.context)
        });

        const data =
            await mapResponseToJson<ActivateProductCrossflowPlanResponse>(
                response
            );

        return data;
    }

    public async activateProductCrossflow<T extends JobContext>(
        request: ActivateProductCrossflowRequest<T>
    ): Promise<ActivateProductCrossflowResponse> {
        const provisioningApiUrl = `/xflow/provision/cloud/${request.cloudId}/product/${request.productKey}/job/${request.jobName}/start`;

        const response = await this.fetch(provisioningApiUrl, {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(request.context)
        });

        const data = await mapResponseToJson<ActivateProductCrossflowResponse>(
            response
        );

        if (!data.started) {
            throw new Error("Provisioning failed");
        }

        return data;
    }

    public async waitForActivateProductCrossflow(
        request: WaitForActivateProductCrossflowRequest
    ): Promise<WaitForActivateProductCrossflowResponse> {
        const response = await this.fetch(
            `/xflow/provision/cloud/${request.cloudId}/product/${request.productKey}/job/${request.jobName}/status`,
            {
                method: "GET",
                credentials: "include"
            }
        );

        return mapResponseToJson<WaitForActivateProductCrossflowResponse>(
            response
        );
    }

    public async postMadlibContentGeneration(
        cloudId: string,
        request: MadlibContentGenerationRequest
    ): Promise<MadlibContentGenerationResponse> {
        const headers: Record<string, string> = {
            "web-platform": "bxp-web-platform",
            "Content-Type": "application/json",
            "X-Atlassian-CloudId": cloudId
        };

        const response = await this.fetch(
            "/gcg/api/v1/generate/team_growth/jira_onboarding_madlibs_sample_issues",
            {
                method: "POST",
                headers,
                credentials: "include",
                body: JSON.stringify(request)
            }
        );

        if (!response.ok) {
            throw new MadlibContentGenerationError(response);
        }

        return mapResponseToJson<MadlibContentGenerationResponse>(response);
    }

    public async postContentGenerationForJiraFfl(
        request: JiraFFLContentGenerationRequest
    ): Promise<JiraFFLContentGenerationResponse> {
        const headers: Record<string, string> = {
            "web-platform": "bxp-web-platform",
            "Content-Type": "application/json",
            // Keep parity with activate-product calls which include recaptcha key as a header.
            "recaptcha-site-key": getReCaptchaKey()
        };

        const response = await this.fetch(
            "/gcg/api/v1/generate/team_growth/jira_ffl_sample_issues",
            {
                method: "POST",
                headers,
                credentials: "include",
                body: JSON.stringify(request)
            }
        );

        return mapResponseToJson<JiraFFLContentGenerationResponse>(response);
    }

    public async getJiraFFLContentByCgsKey(
        cgsKey: string
    ): Promise<JiraFFLContentGenerationResponse> {
        const headers: Record<string, string> = {
            "web-platform": "bxp-web-platform",
            "Content-Type": "application/json"
        };

        const response = await this.fetch(
            `/gcg/api/v1/generate/team_growth/jira_ffl_sample_issues/cache/${cgsKey}`,
            {
                method: "GET",
                headers,
                credentials: "include"
            }
        );

        if (!response.ok) {
            throw new MadlibContentGenerationError(response);
        }

        return mapResponseToJson<JiraFFLContentGenerationResponse>(response);
    }

    public async postEmailInvite(
        cloudId: string,
        projectId: string,
        request: EmailInviteRequest
    ): Promise<EmailInviteResponse> {
        const response = await this.fetch(
            `/ex/jira/${cloudId}/rest/internal/latest/invite-user/request-access/project/${projectId}`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                credentials: "include",
                body: JSON.stringify(request)
            }
        );

        if (!response.ok) {
            throw new UnsuccessfulFetchError({
                message: "Invite was unsuccessful",
                response
            });
        }

        return mapResponseToJson<EmailInviteResponse>(response);
    }
}

export const getStargateBaseUrl = () => {
    if (typeof window !== "undefined") {
        return window.location.hostname.includes("fastshift")
            ? FASTSHIFT_STARGATE_BASE_URL
            : BASE_URL;
    }

    // Add safety check for globalThis.location
    if (globalThis.location) {
        return globalThis.location.hostname.includes("fastshift")
            ? FASTSHIFT_STARGATE_BASE_URL
            : BASE_URL;
    }

    // Fallback if globalThis.location is not available
    return BASE_URL;
};

const BxpWebStargateClientContext = createContext<IBxpWebStargateClient>(
    new BxpWebStargateClient({
        baseUrl: getStargateBaseUrl()
    })
);

export function useBxpWebStargateClient(): IBxpWebStargateClient {
    return useContext(BxpWebStargateClientContext);
}

export type BxpWebStargateClientProviderProps =
    | InterfaceBxpWebStargateClientProviderProps
    | BaseUrlBxpWebStargateClientProviderProps;

export type InterfaceBxpWebStargateClientProviderProps = {
    readonly client: IBxpWebStargateClient;
};

export type BaseUrlBxpWebStargateClientProviderProps = {
    readonly baseUrl: string;
};

export const BxpWebStargateClientProvider = (
    props: PropsWithChildren<BxpWebStargateClientProviderProps>
) => {
    const { children } = props;
    if (isBaseUrlStargateClientProviderProps(props)) {
        return (
            <BxpWebStargateClientContext.Provider
                value={
                    new BxpWebStargateClient({
                        baseUrl: props.baseUrl
                    })
                }
            >
                {children}
            </BxpWebStargateClientContext.Provider>
        );
    } else {
        return (
            <BxpWebStargateClientContext.Provider value={props.client}>
                {children}
            </BxpWebStargateClientContext.Provider>
        );
    }
};

function isBaseUrlStargateClientProviderProps(
    props: BxpWebStargateClientProviderProps
): props is BaseUrlBxpWebStargateClientProviderProps {
    return !!(props as BaseUrlBxpWebStargateClientProviderProps)?.baseUrl;
}
